import { useEffect, useRef, useState } from "react";
import { ArrowClockwise, CaretDown, ArrowCounterClockwise, Shovel } from "@phosphor-icons/react";
import DigCore from "@/components/DigCore";
import PairingBanner from "@/components/PairingBanner";

/**
 * Oct 17, 2026 (Randy) — the dig, as one instrument instead of three words.
 *
 * Before: "Refresh" (an almost invisible icon), "↺ Restart from newest" and
 * "Draft unresponded ↓" all sat at 10px in the same grey toolbar row, each
 * fronted by a browser confirm() wall. Three peers, no hierarchy, and on a
 * phone the confirm covered the screen. They are not peers:
 *
 *   · DIG DEEPER      — the primary act. A real button.
 *   · START OVER      — NOT rare. Randy: "every time they bring in a new video
 *                       there's sort of a need almost to start the dig over",
 *                       because a newly monitored video arrives with its whole
 *                       comment history sitting behind the cursor. So it is a
 *                       co-equal button, and it glows when the backend has
 *                       flagged that new videos landed.
 *   · REFRESH         — ambient. The poller already runs every 90s. Overflow.
 *
 * Progress and results land ON the primary button rather than in a toast that
 * vanishes — the thing that made this invisible on mobile in the first place.
 */

export function DigRig({
  siloId,
  channelSlot,
  siloName,
  integration,
  digMap,
  busy = false,
  loading = false,
  isConnected = true,
  isUnlocked = true,
  digLive = "",
  reachedEnd = false,
  sessionScanned = 0,
  sessionScannedFrom = 0,
  onDig,
  onRestart,
  onRefresh,
  onResolved,
  onOpenSettings,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const vfm = integration?.video_filter_mode || "off";
  const monitoredCount = (integration?.monitored_video_ids || []).length;
  const filterBlocked = vfm === "off" || (vfm === "selected" && monitoredCount === 0);
  const canDig = isConnected && isUnlocked && !filterBlocked;
  const restartUrged = !!digMap?.restart_recommended;
  const mismatched = digMap?.pairing?.state === "mismatched";

  return (
    <div className="mb-3" data-testid={`dig-rig-${channelSlot}`}>
      <PairingBanner
        pairing={digMap?.pairing}
        siloId={siloId}
        channelSlot={channelSlot}
        siloName={siloName}
        onResolved={onResolved}
      />

      <div className="rounded-lg border hairline bg-[#FBFAF7] p-3">
        <DigCore map={digMap} busy={busy} channelSlot={channelSlot} />

        {/* New videos arrived → their whole history sits behind the cursor. */}
        {restartUrged && (
          <div
            className="mt-2.5 flex items-start gap-2 rounded-md border border-[#C9D9E4] bg-[#F2F7FA] px-2.5 py-2"
            data-testid={`dig-restart-nudge-${channelSlot}`}
          >
            <ArrowCounterClockwise size={13} weight="bold" className="text-[#2C6E92] shrink-0 mt-px" />
            <div className="text-[10.5px] leading-snug text-[#2A4A5C]">
              <strong className="font-medium">New ground to cover.</strong>{" "}
              You added {digMap?.restart_reason || "videos"} — every comment those videos
              ever received sits behind where the dig has already been. Start over to sweep
              them in; your existing drafts stay put.
            </div>
          </div>
        )}

        {filterBlocked && (
          <div
            className="mt-2.5 text-[10.5px] leading-snug text-[#8C4A2E] bg-[#FBE9E1] border border-[#E5C4B2] rounded-md px-2.5 py-2"
            data-testid={`dig-filter-blocked-${channelSlot}`}
          >
            {vfm === "off"
              ? "The video filter is off, so there is nothing in scope to dig through."
              : "Selected-videos mode is on but no videos are picked, so every thread is filtered out."}
            {onOpenSettings && (
              <button
                onClick={onOpenSettings}
                className="ml-1 underline hover:no-underline"
                data-testid={`dig-filter-settings-${channelSlot}`}
              >
                Open settings
              </button>
            )}
          </div>
        )}

        {/* Controls. Full-width stack on phones, inline row from sm up. */}
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
          <button
            onClick={() => onDig?.()}
            disabled={busy || loading || !canDig || mismatched}
            data-testid={`yt-console-pull-unresponded-${channelSlot}`}
            className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-[#2C6E92] text-white text-[12.5px] font-medium hover:bg-[#245B79] active:scale-[0.99] transition-all disabled:opacity-45 disabled:cursor-not-allowed"
            title={mismatched
              ? "Held: the connected channel is not the one this silo is paired with. Resolve the pairing above first."
              : "Walks up to ~500 more threads back through this channel's comment history, resuming where the last dig stopped. Drafts land in the inbox as PENDING."}
          >
            {busy ? (
              <ArrowClockwise size={15} weight="bold" className="animate-spin" />
            ) : (
              <Shovel size={15} weight="duotone" />
            )}
            <span className="truncate">
              {mismatched
                ? "Dig held — check the channel above"
                : busy
                  ? (digLive || "Digging…")
                  : digLive || (reachedEnd ? "Dig again from newest" : "Dig deeper")}
            </span>
          </button>

          <button
            onClick={() => onRestart?.()}
            disabled={busy || loading || !canDig || mismatched}
            data-testid={`yt-console-pull-restart-${channelSlot}`}
            className={`inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-[12px] transition-all disabled:opacity-45 disabled:cursor-not-allowed ${
              restartUrged
                ? "bg-[#E8F1F6] border border-[#9FC3D6] text-[#1B4E6E] hover:bg-[#DCEAF2] font-medium"
                : "border hairline bg-white text-[#4A4A48] hover:bg-[#F4F3EE]"
            }`}
            title="Rewinds to your newest comments and digs from there. Use after adding videos — their older comments sit behind the current position. Your existing drafts are untouched."
          >
            <ArrowCounterClockwise size={14} weight="bold" />
            <span>Start over</span>
          </button>

          <div className="relative shrink-0" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              disabled={busy || loading}
              data-testid={`dig-overflow-${channelSlot}`}
              aria-label="More dig actions"
              aria-expanded={menuOpen}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-1 px-2.5 py-2.5 rounded-lg border hairline bg-white text-[#6B6A68] hover:text-[#1C1B1A] hover:bg-[#F4F3EE] transition-colors disabled:opacity-45"
            >
              <CaretDown size={13} weight="bold" />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 top-full mt-1 z-20 w-56 rounded-lg border hairline bg-white shadow-lg py-1"
                data-testid={`dig-overflow-menu-${channelSlot}`}
              >
                <button
                  onClick={() => { setMenuOpen(false); onRefresh?.(); }}
                  data-testid={`yt-console-refresh-${channelSlot}`}
                  className="w-full text-left px-3 py-2 text-[11.5px] text-[#2C2C2A] hover:bg-[#F4F3EE] flex items-center gap-2"
                >
                  <ArrowClockwise size={13} weight="bold" className="text-[#4A5D4E]" />
                  <span>
                    Check for new comments
                    <span className="block text-[10px] text-[#8A8884]">
                      Top of the channel only — runs on its own every 90s
                    </span>
                  </span>
                </button>
                {onOpenSettings && (
                  <button
                    onClick={() => { setMenuOpen(false); onOpenSettings(); }}
                    data-testid={`dig-overflow-settings-${channelSlot}`}
                    className="w-full text-left px-3 py-2 text-[11.5px] text-[#2C2C2A] hover:bg-[#F4F3EE]"
                  >
                    Which videos get dug
                    <span className="block text-[10px] text-[#8A8884]">
                      {vfm === "selected" ? `${monitoredCount} selected` : vfm === "all" ? "All videos" : "Filter is off"}
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Session line — what THIS sitting has walked. */}
        {(sessionScanned > 0 || reachedEnd) && !busy && (
          <div
            className="mt-2 text-[10px] text-[#8A8884] tabular-nums"
            data-testid={`yt-console-session-batched-${channelSlot}`}
          >
            {reachedEnd
              ? "This channel is fully walked — the next dig starts again from your newest comments."
              : `Last dig walked threads ${sessionScannedFrom.toLocaleString()}–${sessionScanned.toLocaleString()}.`}
          </div>
        )}

        {mismatched && (
          <div className="mt-2 text-[10px] text-[#A2611F]" data-testid={`dig-held-${channelSlot}`}>
            Digging is held until the channel pairing above is resolved.
          </div>
        )}
      </div>
    </div>
  );
}

export default DigRig;
